import { BaseApiService } from '../utils/apiClient';
import api from './api';

class ContractorService extends BaseApiService {
  constructor(apiClient) {
    super(apiClient);
  }

  // Get all contractors
  async getContractors(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    const response = await this.get(`/contractors?${params.toString()}`);
    return response;
  }

  // Get contractor by ID
  async getContractor(id) {
    const response = await this.get(`/contractors/${id}`);
    return response;
  }

  // Update own contractor profile
  async updateProfile(profileData) {
    const response = await this.put(`/contractors/profile/me`, profileData);
    return response;
  }

  // Get contractor metrics (response rate, on-time rate, trust score)
  async getContractorMetrics(id) {
    const response = await this.get(`/contractors/${id}/metrics`);
    return response;
  }

  // Calculate trust score based on metrics
  calculateTrustScore(metrics) {
    if (!metrics) return 0;

    const {
      averageRating = 0,
      onTimeRate = 0,
      responseRate = 0,
      disputeResolutionRate = 100
    } = metrics;

    const ratingScore = averageRating * 20;
    const completionScore = onTimeRate;
    const responseScore = responseRate;
    const disputeScore = disputeResolutionRate;

    return Math.round((ratingScore + completionScore + responseScore + disputeScore) / 4);
  }

  // Get contractors with calculated metrics
  async getContractorsWithMetrics(filters = {}) {
    const contractors = await this.getContractors(filters);

    if (contractors.success && contractors.data) {
      const contractorsWithMetrics = await Promise.all(
        contractors.data.map(async (contractor) => {
          try {
            const metrics = await this.getContractorMetrics(contractor.id);
            return {
              ...contractor,
              metrics: metrics.success ? metrics.data : null,
              trustScore: this.calculateTrustScore(metrics.success ? metrics.data : null)
            };
          } catch (error) {
            console.warn(`Failed to get metrics for contractor ${contractor.id}:`, error);
            return {
              ...contractor,
              metrics: null,
              trustScore: 0
            };
          }
        })
      );

      return {
        ...contractors,
        data: contractorsWithMetrics
      };
    }

    return contractors;
  }

  // Get marketplace contractors (delegates to getContractors with optional filters)
  async getMarketplaceContractors(filters = {}) {
    const response = await this.getContractors(filters);
    return response?.data || response || [];
  }

  // Get previous contractors the client has worked with
  async getPreviousContractors(clientId) {
    try {
      const response = await api.get('/projects', { params: { status: 'completed' } });
      const projects = response.data?.data || response.data?.projects || [];
      const contractorMap = new Map();
      for (const project of projects) {
        if (project.contractor_id && project.contractor) {
          contractorMap.set(project.contractor_id, {
            id: project.contractor_id,
            name: `${project.contractor.first_name || ''} ${project.contractor.last_name || ''}`.trim(),
            first_name: project.contractor.first_name,
            last_name: project.contractor.last_name,
            specialization: project.contractor.specialization,
            location: project.contractor.location,
            avatar_url: project.contractor.profile_image_url,
            lastProject: project.title
          });
        }
      }
      return Array.from(contractorMap.values());
    } catch (err) {
      console.error('Error loading previous contractors:', err);
      return [];
    }
  }

  // Notify contractors about a new project (non-critical)
  async notifyNewProject(projectId) {
    try {
      await api.post(`/projects/${projectId}/notify-contractors`);
    } catch (err) {
      console.warn('notifyNewProject failed (non-critical):', err.message);
    }
  }
}

// Default instance using the shared api client (works without createAuthenticatedService)
const contractorServiceInstance = new ContractorService(api);

export { ContractorService };
export default contractorServiceInstance;
